import * as React from 'react'

import { Button, TextSpinner } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import i18n from 'modules/core/lib/i18n'

import { useOnSelectEmployees } from 'modules/shift_management/hooks/useOnSelectEmployees'
import { useSelectableEmployeesByCurrent } from 'modules/shift_management/hooks/useSelectableEmployeesByCurrent'
import ImportEmployeesToSMForm from 'modules/shift_management/lib/forms/ImportEmployeesToSMForm'

import FormContent from './FormContent'
import useImportEmployeesSM from './useImportEmployeesSM'

interface Props {
  onClose: () => void
}

// I need to import employees that I have permissions to create shifts
// but they might not be using shift management yet
const ImportEmployeesModal = ({ onClose }: Props) => {
  const form = useLazyRef(() => new ImportEmployeesToSMForm()).current
  const [employeeIds, setSelectedEmployees] = React.useState<number[]>([])
  const effectiveAt = form.get('effective_at').value
  const numberOfRestDays = form.get('number_of_rest_days_in_cents').value
  const { employeeIds: selectableEmployeeIds } = useSelectableEmployeesByCurrent()
  const selectableEmployeesCount = selectableEmployeeIds.length

  const { onSelectEmployee } = useOnSelectEmployees({
    form,
    selectedEmployees: employeeIds,
    setSelectedEmployees,
  })

  const isComplete = !!form.get('effective_at').value && !!form.get('employee_id').value

  const { onSubmit, submitting } = useImportEmployeesSM({
    effectiveAt,
    employeeIds,
    numberOfRestDays,
    selectableEmployeesCount,
    onSuccess: () => onClose(),
  })

  return (
    <Modal
      illustration="add-user"
      title={i18n.t('shift_management.import_employees.title')}
      description={i18n.t('shift_management.import_employees.description')}
      onSubmit={onSubmit}
      onClose={onClose}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button disabled={submitting || !isComplete} submit>
            {i18n.t('shift_management.import_employees.action')}
          </Button>
        </>
      }
    >
      {submitting && <TextSpinner text={i18n.t('shift_management.actions.adding_employees')} />}
      {!submitting && (
        <FormContent
          form={form}
          onSelectEmployee={onSelectEmployee}
          selectedEmployeeIds={employeeIds}
          selectableEmployeeIds={selectableEmployeeIds}
        />
      )}
    </Modal>
  )
}

export default observer(ImportEmployeesModal)
